/* The Lynqu backlink keeps rel="noopener" (not "noreferrer") so lynqu.com
   receives the referrer for attributed referral traffic + dofollow SEO value. */
/* eslint-disable react/jsx-no-target-blank */
import React from 'react';
import { Link } from 'react-router-dom';
import { Link as ScrollLink } from 'react-scroll';
import { FOCUS_RING } from '../utils/a11y';
// Light-on-dark wordmark for the dark footer (same asset the nav uses on dark).
import Logo from '../../assets/logo_dark.webp';
import LogoX from '../../assets/footer/logo_x.svg';
// Monochrome glyph versions (the brand SVGs were filled "badge" art that
// flattened to white squares under the white-invert filter).
import LogoYoutube from '../../assets/footer/logo_youtube_mono.webp';
import LogoThreads from '../../assets/footer/logo_threads_mono.webp';
import LogoMedium from '../../assets/footer/logo_medium.svg';
import LogoLinkedin from '../../assets/footer/logo_linkedin.svg';
import LogoInstagram from '../../assets/footer/logo_instagram.svg';
import LogoFacebook from '../../assets/footer/logo_facebook.svg';
import LogoTiktok from '../../assets/footer/logo_tiktok.svg';

// All marks are monochrome glyphs (Threads/YouTube now use mono webp assets),
// so every icon inverts to a uniform white.
const SOCIALS = [
    { logo: LogoLinkedin, label: 'LinkedIn', href: 'https://www.linkedin.com/company/gravisun' },
    { logo: LogoThreads, label: 'Threads', href: 'https://www.threads.com/@gravisun' },
    { logo: LogoX, label: 'X', href: 'https://x.com/gravisun' },
    { logo: LogoInstagram, label: 'Instagram', href: 'https://www.instagram.com/gravisun/' },
    { logo: LogoFacebook, label: 'Facebook', href: 'https://www.facebook.com/gravisun' },
    { logo: LogoMedium, label: 'Medium', href: 'https://medium.com/gravisun' },
    { logo: LogoTiktok, label: 'TikTok', href: 'https://www.tiktok.com/@gravisun' },
    { logo: LogoYoutube, label: 'YouTube', href: 'https://www.youtube.com/channel/UCQqRBGCsjDuhfUQtSbiIBZg' },
];

// react-scroll targets resolve against each section's `id`.
const QUICK_LINKS = [
    { name: 'Services', to: 'services' },
    { name: 'Projects', to: 'projects' },
    { name: 'Products', to: 'product' },
    { name: 'Approach', to: 'approach' },
    { name: 'Team', to: 'team' },
];

export default function Footer({ sectionRef }) {
    return (
        <section
            ref={sectionRef}
            className='relative w-full overflow-hidden bg-[#0e0d12] text-white'>
            {/* Warm sun glow bleeding from the bottom-left — echoes the hero. */}
            <div
                className='pointer-events-none absolute -bottom-[34%] -left-[8%] w-[52%] aspect-square rounded-full'
                style={{ background: 'radial-gradient(circle, rgba(225,85,17,0.16), transparent 62%)' }}
            />
            {/* Hairline orange top accent. */}
            <div className='pointer-events-none absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#E15511]/45 to-transparent' />

            <div className='relative z-10 max-w-[1200px] mx-auto px-[24px] c-lg:px-[40px] pt-[64px] c-lg:pt-[88px] pb-[40px]'>
                {/* ===== CTA band ===== */}
                <div
                    data-reveal
                    className='flex flex-col c-md:flex-row c-md:items-end c-md:justify-between gap-[28px] pb-[44px] border-b border-white/10'>
                    <div>
                        <h2 className='text-28 c-lg:text-42 font-bold leading-[1.1] tracking-[-0.01em]'>
                            Let&apos;s build something{' '}
                            <span className='text-[#F0976B]'>people love.</span>
                        </h2>
                        <p className='mt-[14px] text-15 c-lg:text-16 text-white/55 max-w-[440px] leading-[1.55]'>
                            Have a product in mind? Tell us about it — we usually reply
                            within one business day.
                        </p>
                    </div>
                    <a
                        href='mailto:info@gravisun.com'
                        className={`group inline-flex items-center justify-center gap-[9px] rounded-full bg-[#CB4909] hover:bg-[#B33D06] text-white font-semibold text-15 px-[26px] py-[15px] min-h-[50px] w-fit transition-[background-color,transform] duration-200 ease-out hover:-translate-y-[2px] shadow-[0_14px_30px_-10px_rgba(225,85,17,0.6)] ${FOCUS_RING}`}>
                        Start a project
                        <svg viewBox='0 0 24 24' width='18' height='18' fill='none' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' className='transition-transform duration-300 group-hover:translate-x-[3px]'>
                            <path d='M5 12h14' /><path d='m13 6 6 6-6 6' />
                        </svg>
                    </a>
                </div>

                {/* ===== Main grid ===== */}
                <div
                    data-reveal
                    style={{ '--reveal-delay': '80ms' }}
                    className='grid grid-cols-1 c-md:grid-cols-[1.5fr_1fr_1.1fr] gap-[40px] c-lg:gap-[56px] py-[48px]'>
                    {/* Brand */}
                    <div className='flex flex-col items-start'>
                        <img src={Logo} alt='Gravisun' width={317} height={108} className='w-[176px] h-auto' />
                        <p className='mt-[20px] text-14 text-white/50 leading-[1.65] max-w-[330px]'>
                            A full-stack product studio crafting elegant,
                            high-performance software across mobile, web, backend
                            &amp; cloud.
                        </p>
                        {/* Entity/trust facts mirror the Organization JSON-LD in
                            public/index.html — keep the two in sync. */}
                        <p className='mt-[16px] text-13 text-white/40 leading-[1.6]'>
                            Gravisun Soft Kft. · Budapest, Hungary · Building since 2019
                        </p>
                        <p className='mt-[16px] text-14 text-white/50 leading-[1.6] max-w-[330px]'>
                            Proudly building{' '}
                            <a
                                href='https://lynqu.com'
                                target='_blank'
                                rel='noopener'
                                className='text-[#F0976B] font-semibold hover:underline underline-offset-4'>
                                Lynqu
                            </a>
                            , our networking &amp; lead-capture platform, and{' '}
                            <a
                                href='https://flatrentbudapest.com'
                                target='_blank'
                                rel='noopener'
                                className='text-[#F0976B] font-semibold hover:underline underline-offset-4'>
                                Flat Rent Budapest
                            </a>
                            , the owner-direct home marketplace for our city.
                        </p>
                    </div>

                    {/* Quick links */}
                    <div>
                        <h3 className='text-12 font-semibold tracking-[0.2em] uppercase text-white/35'>
                            Explore
                        </h3>
                        <ul className='mt-[20px] flex flex-col gap-[13px]'>
                            {QUICK_LINKS.map((l) => (
                                <li key={l.to}>
                                    {/* Mirror the nav's scroll config (no `smooth`/`offset`):
                                        react-scroll's animated scroll fights the page's
                                        scroll-snap and lands on the wrong section. */}
                                    {/* href makes the anchor crawlable; react-scroll
                                        preventDefault()s the native fragment jump. */}
                                    {/* py + negative my = 44px touch target without
                                        changing the visual rhythm of the list. */}
                                    <ScrollLink
                                        href={`#${l.to}`}
                                        to={l.to}
                                        duration={300}
                                        className={`inline-flex items-center gap-[8px] py-[11px] -my-[11px] text-15 text-white/65 hover:text-white cursor-pointer transition-colors duration-200 group ${FOCUS_RING} rounded`}>
                                        <span className='h-px w-[18px] origin-left scale-x-[0.55] bg-white/25 group-hover:scale-x-100 group-hover:bg-[#E15511] transition-[transform,background-color] duration-200' />
                                        {l.name}
                                    </ScrollLink>
                                </li>
                            ))}
                            <li>
                                <Link
                                    to='/blog'
                                    className={`inline-flex items-center gap-[8px] py-[11px] -my-[11px] text-15 text-white/65 hover:text-white cursor-pointer transition-colors duration-200 group ${FOCUS_RING} rounded`}>
                                    <span className='h-px w-[18px] origin-left scale-x-[0.55] bg-white/25 group-hover:scale-x-100 group-hover:bg-[#E15511] transition-[transform,background-color] duration-200' />
                                    Blog
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Social + Clutch */}
                    <div>
                        <h3 className='text-12 font-semibold tracking-[0.2em] uppercase text-white/35'>
                            Follow us
                        </h3>
                        <div className='mt-[20px] flex flex-wrap gap-[10px]'>
                            {SOCIALS.map((s) => (
                                <a
                                    key={s.label}
                                    href={s.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    aria-label={`Gravisun on ${s.label}`}
                                    className={`group inline-flex items-center justify-center size-[42px] rounded-full bg-white/[0.06] border border-white/10 hover:bg-white/[0.12] hover:border-white/20 transition-[background-color,border-color,transform] duration-200 ease-out hover:-translate-y-[3px] ${FOCUS_RING}`}>
                                    {/* Monochrome glyphs inverted to a uniform white. */}
                                    <img
                                        src={s.logo}
                                        alt=''
                                        width={18}
                                        height={18}
                                        loading='lazy'
                                        className='size-[18px] opacity-70 group-hover:opacity-100 transition-opacity duration-200 [filter:brightness(0)_invert(1)]'
                                    />
                                </a>
                            ))}
                        </div>

                        {/* Static badge (was the Clutch script widget in an
                            iframe): the third-party script can't be SRI-pinned
                            and its white canvas showed as a sliver on the dark
                            card. With a single review there's nothing live to
                            lose — update the stars/count here if it changes. */}
                        <a
                            href='https://clutch.co/profile/gravisun'
                            target='_blank'
                            rel='noopener noreferrer'
                            aria-label='Gravisun on Clutch — rated 4.5 stars from 1 review'
                            className={`mt-[28px] inline-flex items-center gap-[14px] rounded-[14px] bg-white/[0.05] border border-white/10 hover:border-white/25 px-[16px] py-[12px] transition-colors duration-200 ${FOCUS_RING}`}>
                            <span className='flex flex-col gap-[5px]'>
                                <span className='flex items-center gap-[2px]' aria-hidden='true'>
                                    {[0, 1, 2, 3].map((i) => (
                                        <svg key={i} viewBox='0 0 24 24' width='15' height='15' fill='#E62415' aria-hidden='true'>
                                            <path d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z' />
                                        </svg>
                                    ))}
                                    <svg viewBox='0 0 24 24' width='15' height='15' aria-hidden='true'>
                                        <defs>
                                            <linearGradient id='clutch-half-star'>
                                                <stop offset='50%' stopColor='#E62415' />
                                                <stop offset='50%' stopColor='rgba(255,255,255,0.25)' />
                                            </linearGradient>
                                        </defs>
                                        <path fill='url(#clutch-half-star)' d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z' />
                                    </svg>
                                </span>
                                <span className='text-12 tracking-[0.1em] text-white/55'>4.5 · 1 REVIEW</span>
                            </span>
                            <span className='text-12 text-white/45 leading-[1.4] max-w-[96px]'>
                                Reviewed on Clutch
                            </span>
                        </a>
                    </div>
                </div>

                {/* ===== Bottom bar ===== */}
                <div
                    data-reveal
                    className='flex flex-col-reverse c-md:flex-row items-center c-md:justify-between gap-[16px] pt-[30px] border-t border-white/10'>
                    <p className='text-13 text-white/40'>
                        © {new Date().getFullYear()} Gravisun Soft Kft. All rights reserved.
                    </p>
                    <div className='flex items-center gap-[20px] text-13 text-white/55'>
                        <Link to='/privacy' className='inline-flex items-center min-h-[44px] hover:text-white transition-colors duration-200'>
                            Privacy
                        </Link>
                        <span aria-hidden='true' className='text-white/20'>·</span>
                        <Link to='/terms' className='inline-flex items-center min-h-[44px] hover:text-white transition-colors duration-200'>
                            Terms
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}
